import { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Megaphone, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { signalsService } from '../../services/signals.service';
import type { SendSignalFromAnalysisInput } from '../../services/signals.service';
import { formatPrice, getSymbolName } from '@trading/shared';

type ApiErrorPayload = {
  error?: {
    code?: string;
    message?: string;
    details?: Record<string, { message?: string }>;
  };
};

function describeApiError(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null;
  const data = err.response?.data as ApiErrorPayload | undefined;
  const apiErr = data?.error;
  if (!apiErr) return null;
  const details = apiErr.details;
  if (details && typeof details === 'object') {
    const fieldMessages = Object.entries(details)
      .map(([field, info]) => {
        const msg = info?.message ?? '';
        return msg ? `${field}: ${msg}` : '';
      })
      .filter(Boolean)
      .join(' · ');
    if (fieldMessages.length > 0) return fieldMessages;
  }
  return apiErr.message ?? null;
}

const EXPERT_NOTES_MAX = 500;

interface SendSignalConfirmModalProps {
  input: SendSignalFromAnalysisInput;
  pricePrecision: number;
  onClose: () => void;
}

export default function SendSignalConfirmModal({
  input,
  pricePrecision,
  onClose,
}: SendSignalConfirmModalProps) {
  const { t } = useTranslation('analysis');
  const [isSending, setIsSending] = useState(false);
  const [expertNotes, setExpertNotes] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const isLong = input.direction === 'LONG';

  // Track latest isSending / onClose without re-running the mount-only
  // effect. Re-running it (or running it on every parent re-render that
  // ships a fresh `onClose`) used to call focusables[0]?.focus() again,
  // which stole focus away from the expertNotes textarea on every keystroke
  // — on mobile that toggles the soft keyboard open/closed in a loop.
  const isSendingRef = useRef(isSending);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    isSendingRef.current = isSending;
  }, [isSending]);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Mount-only: initial focus, body scroll lock, focus restore, key handler.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const node = containerRef.current;
    if (!node) return;

    const focusables = node.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    focusables[0]?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (!isSendingRef.current) onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  const handleSend = async () => {
    setIsSending(true);
    try {
      const trimmedNotes = expertNotes.trim();
      const result = await signalsService.sendSignalFromAnalysis({
        ...input,
        expertNotes: trimmedNotes.length > 0 ? trimmedNotes : undefined,
      });
      const count = result.notifiedCount;
      if (count !== undefined && count > 0) {
        toast.success(t('sendSignal.toast.success', { count }));
      } else {
        toast.success(t('sendSignal.toast.successGeneric'));
      }
      onClose();
    } catch (err) {
      const detail = describeApiError(err);
      const base = t('sendSignal.toast.failure');
      toast.error(detail ? `${base}: ${detail}` : base, { duration: 8000 });
      setIsSending(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="send-signal-title"
      aria-describedby="send-signal-desc"
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
    >
      {/* Backdrop */}
      <div
        data-testid="send-signal-backdrop"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={isSending ? undefined : onClose}
      />

      <div
        ref={containerRef}
        className="relative w-full max-w-md bg-bg-secondary border border-border-default rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Accent stripe */}
        <div className="h-1 bg-gradient-to-r from-accent-primary via-accent-secondary to-accent-primary" />

        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-accent-primary/15 flex items-center justify-center flex-shrink-0">
              <Megaphone className="w-5 h-5 text-accent-primary" aria-hidden="true" />
            </div>
            <h2
              id="send-signal-title"
              className="text-lg font-semibold text-text-primary"
            >
              {t('sendSignal.modal.title')}
            </h2>
          </div>

          {/* Signal preview */}
          <div
            id="send-signal-desc"
            className="mb-4 p-4 bg-bg-tertiary rounded-xl space-y-3"
          >
            {/* Direction badge + asset */}
            <div className="flex items-center justify-between">
              <span className="font-semibold text-text-primary">{getSymbolName(input.asset)}</span>
              <Badge variant={isLong ? 'success' : 'danger'}>
                <span className="flex items-center gap-1">
                  {isLong
                    ? <TrendingUp className="w-3 h-3" aria-hidden="true" />
                    : <TrendingDown className="w-3 h-3" aria-hidden="true" />}
                  {input.direction}
                </span>
              </Badge>
            </div>

            {/* Key levels */}
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="text-center">
                <p className="text-text-muted mb-0.5">{t('results.entry')}</p>
                <p className="font-mono font-semibold text-text-primary">
                  {formatPrice(input.entryPrice, pricePrecision)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-text-muted mb-0.5">{t('results.stopLoss')}</p>
                <p className="font-mono font-semibold text-loss">
                  {formatPrice(input.stopLoss, pricePrecision)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-text-muted mb-0.5">{t('results.target', { index: 1 })}</p>
                <p className="font-mono font-semibold text-profit">
                  {formatPrice(input.takeProfit1, pricePrecision)}
                </p>
              </div>
            </div>

            {/* Confidence + timeframe */}
            <div className="flex items-center justify-between text-xs text-text-muted">
              <span>{input.timeframe}</span>
              <span>{t('results.confidence', { percent: input.confidence })}</span>
            </div>
          </div>

          {/* Body */}
          <p className="text-sm text-text-secondary leading-relaxed mb-4">
            {t('sendSignal.modal.body')}
          </p>

          {/* Disclaimer */}
          <div className="flex items-start gap-2 p-3 bg-loss/10 border border-loss/20 rounded-lg mb-4">
            <AlertTriangle className="w-4 h-4 text-loss flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-xs text-text-secondary leading-relaxed">
              {t('sendSignal.modal.disclaimer')}
            </p>
          </div>

          {/* Expert Notes */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-1.5">
              <label
                htmlFor="expert-notes"
                className="text-sm font-medium text-text-secondary"
              >
                {t('sendSignal.notes.label')}
              </label>
              <span
                className={`text-xs tabular-nums ${
                  expertNotes.length >= EXPERT_NOTES_MAX ? 'text-loss' : 'text-text-muted'
                }`}
                aria-live="polite"
              >
                {t('sendSignal.notes.charCount', { count: expertNotes.length })}
              </span>
            </div>
            <textarea
              id="expert-notes"
              value={expertNotes}
              onChange={(e) => setExpertNotes(e.target.value)}
              maxLength={EXPERT_NOTES_MAX}
              disabled={isSending}
              placeholder={t('sendSignal.notes.placeholder')}
              rows={3}
              className="w-full bg-bg-tertiary border border-border-default rounded-lg px-3 py-2 text-sm text-text-primary placeholder-text-muted resize-none focus:outline-none focus:ring-1 focus:ring-accent-primary focus:border-accent-primary disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={t('sendSignal.notes.label')}
            />
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isSending}
              className="flex-1"
            >
              {t('sendSignal.modal.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => { void handleSend(); }}
              loading={isSending}
              className="flex-1 gap-2"
            >
              <Megaphone className="w-4 h-4" aria-hidden="true" />
              {t('sendSignal.modal.confirm')}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
